import React from "react";
import { Dropdown } from "antd";
import { useHistory, useLocation } from "react-router-dom";
import { DropdownMenuButton } from "components/ui";
import styled from "styled-components/macro";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { Link } from "components";

import * as ROUTES from "common/routes.constants";
import useAuth from "contexts/Auth/useAuth";
import useOrganizations from "contexts/Organizations/useOrganizations";
import useCan from "hooks/useCan";
import isHegiasStaffEmail from "utils/isHegiasStaffEmail";
import useIsB2cContext from "hooks/useIsB2cContext";

const UserMenu = () => {
	const history = useHistory();
	const { t } = useTranslation();
	const { search } = useLocation();
	const { logout, me, loading: authLoading } = useAuth();
	const { current, member, user } = useOrganizations();

	// The org admin area manages members, the subscription, the org profile and
	// project assignments — a personal space has none of those: one implicit
	// member, no plan of its own, one project. The user owns it, so the role
	// check alone would let them in on every one of those pages. ProtectedRoute
	// closes the URL too, so this is the menu half of one guard, not the guard.
	const adminMenuVisible =
		useCan(["admin", "owner"], { anyOf: true }) && !current?.isPersonal;
	// The internal staff dashboard. Shown on the account's email, not on an org
	// role — it is a HEGIAS page, not a customer one. The server re-checks.
	const internalMenuVisible = isHegiasStaffEmail(me?.profile?.email);
	// The Library is a whole-catalog browsing feature that doesn't apply to a
	// single-project consumer workspace — hide it entirely.
	//
	// Shared hook rather than a local check: this was a third hand-rolled copy,
	// and it compared against the PROD BMS org id alone. On stage and on the
	// internal test org that never matched, so a BMS endcustomer kept the Library
	// link there while a personal-space user (matched on isPersonal, which has no
	// environment to drift) correctly lost it. isBmsOrganizationId in the hook
	// covers all three.
	const isB2cUser = useIsB2cContext();

	const handleLogout = async () => {
		await logout();
		history.push({
			pathname: `/${ROUTES.LOGIN}`,
			search,
		});
	};

	const buildAbbrName = () => {
		const abbrName = user?.profile.name ? user?.profile.name.charAt(0) : "";
		const abbrLastname = user?.profile.lastName
			? user?.profile.lastName.charAt(0)
			: "";
		return `${abbrName}${abbrLastname}`;
	};

	const avatarProps = user?.profile?.avatar?.length
		? {
				src: `${process.env.REACT_APP_AWS_S3_URL}/users/${user?.shortid}/${user?.profile.avatar}`,
		  }
		: { text: buildAbbrName() };

	const menuItems = React.useMemo(() => {
		const items: any[] = [];

		if (!isB2cUser) {
			items.push(
				{
					key: ROUTES.LIBRARY,
					label: (
						<Link
							underline={false}
							to={{
								pathname: `/${ROUTES.LIBRARY}`,
								search,
							}}
						>
							{String(t("app.MainNavigation.menu.library"))}
						</Link>
					),
				},
				{
					type: 'divider',
				},
			);
		}

		if (adminMenuVisible) {
			items.push({
				key: ROUTES.ADMIN,
				label: (
					<Link
						underline={false}
						to={{
							pathname: `/${ROUTES.ADMIN}`,
						}}
					>
						{String(t("app.AvatarNavigation.menu.admin"))}
					</Link>
				),
			});
		}

		if (internalMenuVisible) {
			items.push({
				key: ROUTES.INTERNAL,
				label: (
					<Link
						underline={false}
						to={{
							pathname: `/${ROUTES.INTERNAL}`,
						}}
					>
						Internal
					</Link>
				),
			});
		}

		items.push(
			{
				key: ROUTES.ACCOUNT,
				label: (
					<Link
						underline={false}
						to={{
							pathname: `/${ROUTES.ACCOUNT}`,
						}}
					>
						{String(t("app.AvatarNavigation.menu.account"))}
					</Link>
				),
			},
			{
				type: 'divider',
			},
			{
				key: 'logout',
				label: <span>{String(t("app.AvatarNavigation.menu.logout"))}</span>,
				onClick: handleLogout,
			}
		);

		return items;
	}, [adminMenuVisible, internalMenuVisible, isB2cUser, search, t, handleLogout]);

	const menuTrigger = isMobile ? "click" : "hover";

	if (authLoading || !me || me._id !== user?._id) {
		return null;
	}

	return (
		<StyledDropdown
			menu={{ items: menuItems }}
			trigger={[menuTrigger]}
			overlayClassName="user-menu-dropdown"
			// Above the Navigator tab's overlay buttons (z-index 9999 in PStreaming,
			// to beat the streaming SDK overlays) — antd's default 1050 sits below them.
			overlayStyle={{ zIndex: 10000 }}
		>
			<span onClick={(e) => e.preventDefault()}>
				<DropdownMenuButton
					maxWidth="100px"
					name={user?.profile.name || ""}
					title={member?.role || ""}
					avatarProps={avatarProps}
				/>
			</span>
		</StyledDropdown>
	);
};

const StyledDropdown = styled(Dropdown)`
	cursor: pointer;
	> div {
		align-items: center;
		overflow: hidden;

		> div:first-child {
			min-width: 33px;
			height: 33px;
			width: 33px;
			min-height: 33px;
		}
		> div:last-child {
			margin-right: 5px;
			> * {
				line-height: 16px;
			}
			h4 {
				font-size: 14px;
			}
		}
	}

	@media (max-width: 415px) {
		> div > div:last-child {
			display: none;
		}
	}

`;

export default UserMenu;
